import { createApi } from "@reduxjs/toolkit/query/react";
import {
    AssessmentsResponse,
    ContentEditLog,
    EditLogsQuery,
    LevelByIdResponse,
    LevelsCountResponse,
    LevelsFiltersResponse,
    LevelsListQuery,
    LevelsResponse,
    ModulesResponse,
    PaginatedResponse,
    StatementsCountResponse,
    StatementsListQuery,
    StatementsResponse,
    TopicsResponse,
    UpdateAssessmentBaseFieldsBody,
    UpdateAssessmentDictionaryFieldsBody,
    UpdateLevelDictionaryFieldsBody,
    UpdateModuleDictionaryFieldsBody,
    UpdateTipBaseFieldsBody,
} from "pages/Content/types";

import { baseQueryWithIntercept } from "../connection/api";

const ENDPOINT_PREFIX = "contentEditor";

export const contentEditorApi = createApi({
    reducerPath: "contentEditorApi",
    baseQuery: baseQueryWithIntercept,
    tagTypes: [
        "contentEditorTopics",
        "contentEditorTopicTips",
        "contentEditorTopicStatements",
        "contentEditorAssessments",
        "contentEditorEditLogs",
        "contentEditorModules",
        "contentEditorLevels",
        "contentEditorLevelsCount",
        "contentEditorLevelsFilters",
        "contentEditorLevelById",
        "contentEditorStatements",
        "contentEditorStatementsCount",
    ],
    endpoints: build => ({
        getContentEditLogs: build.query<PaginatedResponse<ContentEditLog>, EditLogsQuery | void>({
            query: args => {
                const params = new URLSearchParams();
                const q = args ?? {};
                if (q.page) params.set("page", String(q.page));
                if (q.limit) params.set("limit", String(q.limit));
                if (q.sort) params.set("sort", q.sort);
                if (q.entityType) params.set("entityType", q.entityType);
                if (q.eventType) params.set("eventType", q.eventType);
                if (q.lang) params.set("lang", q.lang);
                if (q.userId) params.set("userId", q.userId);
                if (q.entityId) params.set("entityId", q.entityId);
                if (q.q) params.set("q", q.q);
                if (q.from) params.set("from", q.from);
                if (q.to) params.set("to", q.to);

                const qs = params.toString();
                return {
                    url: `${ENDPOINT_PREFIX}/edit-logs${qs ? `?${qs}` : ""}`,
                    method: "GET",
                };
            },
            providesTags: r => (r?.docs?.length ? [{ type: "contentEditorEditLogs", id: "LIST" }] : [{ type: "contentEditorEditLogs", id: "LIST" }]),
        }),
        getTopicsData: build.query<TopicsResponse, void>({
            query: () => ({ url: `${ENDPOINT_PREFIX}/topics` }),
            providesTags: ["contentEditorTopics"],
        }),
        getAssessmentsData: build.query<AssessmentsResponse, void>({
            query: () => ({ url: `${ENDPOINT_PREFIX}/assessments` }),
            providesTags: ["contentEditorAssessments"],
        }),

        getTopicTipsData: build.query<any, string[]>({
            query: ids => ({ url: `${ENDPOINT_PREFIX}/tips`, method: "POST", body: { ids } }),
            providesTags: (_r, _e, ids) => ids.map(id => ({ type: "contentEditorTopicTips", id })),
        }),

        getTopicStatementsData: build.query<any, string[]>({
            query: ids => ({ url: `${ENDPOINT_PREFIX}/statements`, method: "POST", body: { ids } }),
            providesTags: (_r, _e, ids) => ids.map(id => ({ type: "contentEditorTopicStatements", id })),
        }),

        updateTopicDictionaryStatus: build.mutation<any, { topicId: string; lang: string; status: "verified" | "unverified" | "AI-generated" | "AI-checked" }>({
            query: ({ topicId, lang, status }) => ({
                url: `${ENDPOINT_PREFIX}/topics/${topicId}/dictionary/${lang}/status`,
                method: "PATCH",
                body: { status },
            }),
            async onQueryStarted({ topicId, lang, status }, { dispatch, queryFulfilled }) {
                const patch = dispatch(
                    contentEditorApi.util.updateQueryData("getTopicsData", undefined, (draft: any) => {
                        const t = draft?.topics?.find((x: any) => x._id === topicId);
                        if (t) {
                            t.dictionary = t.dictionary || {};
                            t.dictionary[lang] = t.dictionary[lang] || {};
                            t.dictionary[lang].status = status;
                        }
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patch.undo();
                }
            },
        }),

        updateTopicDictionaryFields: build.mutation<any, { topicId: string; lang: string; patch: Partial<{ name: string; description: string }> }>({
            query: ({ topicId, lang, patch }) => ({
                url: `${ENDPOINT_PREFIX}/topics/${topicId}/dictionary/${lang}`,
                method: "PATCH",
                body: patch,
            }),
            async onQueryStarted({ topicId, lang, patch }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    contentEditorApi.util.updateQueryData("getTopicsData", undefined, (draft: any) => {
                        const t = draft?.topics?.find((x: any) => x._id === topicId);
                        if (!t) return;
                        t.dictionary = t.dictionary || {};
                        t.dictionary[lang] = t.dictionary[lang] || {};
                        if (patch.name !== undefined) t.dictionary[lang].name = patch.name;
                        if (patch.description !== undefined) t.dictionary[lang].description = patch.description;
                        t.dictionary[lang].status = "unverified";
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),

        updateAssessmentDictionaryStatus: build.mutation<any, { assessmentId: string; lang: string; status: string }>({
            query: ({ assessmentId, lang, status }) => ({
                url: `${ENDPOINT_PREFIX}/assessments/${assessmentId}/dictionary/${lang}/status`,
                method: "PATCH",
                body: { status },
            }),
            async onQueryStarted({ assessmentId, lang, status }, { dispatch, queryFulfilled }) {
                const patch = dispatch(
                    contentEditorApi.util.updateQueryData("getAssessmentsData", undefined, (draft: any) => {
                        const t = draft?.assessments?.find((x: any) => x._id === assessmentId);
                        if (t) {
                            t.dictionary = t.dictionary || {};
                            t.dictionary[lang] = t.dictionary[lang] || {};
                            t.dictionary[lang].status = status;
                        }
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patch.undo();
                }
            },
        }),

        updateAssessmentDictionaryFields: build.mutation<any, UpdateAssessmentDictionaryFieldsBody>({
            query: ({ assessmentId, lang, patch }) => ({
                url: `${ENDPOINT_PREFIX}/assessments/${assessmentId}/dictionary/${lang}`,
                method: "PATCH",
                body: patch,
            }),
            async onQueryStarted({ assessmentId, lang, patch }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    contentEditorApi.util.updateQueryData("getAssessmentsData", undefined, (draft: any) => {
                        const t = draft?.assessments?.find((x: any) => x._id === assessmentId);
                        if (!t) return;
                        t.dictionary = t.dictionary || {};
                        t.dictionary[lang] = t.dictionary[lang] || {};
                        if (patch.name !== undefined) t.dictionary[lang].name = patch.name;
                        if (patch.description !== undefined) t.dictionary[lang].description = patch.description;
                        if (patch.question !== undefined) t.dictionary[lang].question = patch.question;
                        if (patch.message !== undefined) t.dictionary[lang].message = patch.message;
                        if (patch.options !== undefined) t.dictionary[lang].options = patch.options;
                        t.dictionary[lang].status = "unverified";
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),

        updateAssessmentBaseFields: build.mutation<any, UpdateAssessmentBaseFieldsBody>({
            query: ({ assessmentId, patch }) => ({
                url: `${ENDPOINT_PREFIX}/assessments/${assessmentId}/base`,
                method: "PATCH",
                body: patch,
            }),
            async onQueryStarted({ assessmentId, patch }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    contentEditorApi.util.updateQueryData("getAssessmentsData", undefined, (draft: any) => {
                        const a = draft?.assessments?.find((x: any) => x._id === assessmentId);
                        if (!a) return;
                        if ("type" in patch) a.type = patch.type ?? null;
                        if ("isPositive" in patch) a.isPositive = patch.isPositive ?? null;
                        if ("min" in patch) a.min = patch.min ?? null;
                        if ("max" in patch) a.max = patch.max ?? null;
                        if ("significantImprovement" in patch) a.significantImprovement = patch.significantImprovement ?? null;
                        if ("threshold" in patch) a.threshold = patch.threshold ?? null;
                        if ("adjustment" in patch) a.adjustment = patch.adjustment ?? null;
                        a.updatedAt = new Date().toISOString();
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),

        getModulesData: build.query<ModulesResponse, void>({
            query: () => ({ url: `${ENDPOINT_PREFIX}/modules` }),
            providesTags: ["contentEditorModules"],
        }),

        updateModuleDictionaryStatus: build.mutation<
            any,
            { moduleId: string; lang: string; status: "verified" | "unverified" | "AI-generated" | "AI-checked" }
        >({
            query: ({ moduleId, lang, status }) => ({
                url: `${ENDPOINT_PREFIX}/modules/${moduleId}/dictionary/${lang}/status`,
                method: "PATCH",
                body: { status },
            }),
            async onQueryStarted({ moduleId, lang, status }, { dispatch, queryFulfilled }) {
                const patch = dispatch(
                    contentEditorApi.util.updateQueryData("getModulesData", undefined, (draft: any) => {
                        const m = draft?.modules?.find((x: any) => x._id === moduleId);
                        if (m) {
                            m.dictionary = m.dictionary || {};
                            m.dictionary[lang] = m.dictionary[lang] || {};
                            m.dictionary[lang].status = status;
                        }
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patch.undo();
                }
            },
        }),

        updateModuleDictionaryFields: build.mutation<any, UpdateModuleDictionaryFieldsBody>({
            query: ({ moduleId, lang, patch }) => ({
                url: `${ENDPOINT_PREFIX}/modules/${moduleId}/dictionary/${lang}`,
                method: "PATCH",
                body: patch,
            }),
            async onQueryStarted({ moduleId, lang, patch }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    contentEditorApi.util.updateQueryData("getModulesData", undefined, (draft: any) => {
                        const m = draft?.modules?.find((x: any) => x._id === moduleId);
                        if (!m) return;

                        if (lang === "en") {
                            const prevEn = m.dictionary?.en ?? {};
                            const enEntry = {
                                name: patch.name !== undefined ? patch.name : prevEn.name ?? "",
                                description: patch.description !== undefined ? patch.description : prevEn.description ?? "",
                                descriptionLong: patch.descriptionLong !== undefined ? patch.descriptionLong : prevEn.descriptionLong ?? "",
                                status: "verified",
                            };
                            m.dictionary = { en: enEntry };
                            m.shouldTranslate = true;
                            return;
                        }

                        m.dictionary = m.dictionary || {};
                        m.dictionary[lang] = m.dictionary[lang] || {};
                        if (patch.name !== undefined) m.dictionary[lang].name = patch.name;
                        if (patch.description !== undefined) m.dictionary[lang].description = patch.description;
                        if (patch.descriptionLong !== undefined) m.dictionary[lang].descriptionLong = patch.descriptionLong;
                        m.dictionary[lang].status = "unverified";
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),

        getStatementsData: build.query<StatementsResponse, StatementsListQuery | void>({
            query: args => {
                const params = new URLSearchParams();
                const a = args ?? {};
                if (a.page) params.set("page", String(a.page));
                if (a.limit) params.set("limit", String(a.limit));
                if (a.topicId) params.set("topicId", a.topicId);
                if (a.lang) params.set("lang", a.lang);
                if (a.status) params.set("status", a.status);
                if (a.q) params.set("q", a.q);
                if (a.sort) params.set("sort", a.sort);
                const qs = params.toString();
                return { url: `${ENDPOINT_PREFIX}/statements${qs ? `?${qs}` : ""}` };
            },
            providesTags: ["contentEditorStatements"],
        }),

        getStatementsCount: build.query<StatementsCountResponse, void>({
            query: () => ({ url: `${ENDPOINT_PREFIX}/statements/count` }),
            providesTags: ["contentEditorStatementsCount"],
        }),

        updateStatementDictionaryStatus: build.mutation<
            any,
            { statementId: string; lang: string; status: "verified" | "unverified" | "AI-generated" | "AI-checked" }
        >({
            query: ({ statementId, lang, status }) => ({
                url: `${ENDPOINT_PREFIX}/statements/${statementId}/dictionary/${lang}/status`,
                method: "PATCH",
                body: { status },
            }),
            async onQueryStarted({ statementId, lang, status }, { dispatch, getState, queryFulfilled }) {
                const topicArgsList = contentEditorApi.util.selectCachedArgsForQuery(getState(), "getTopicStatementsData") as string[][];

                const topicPatches = topicArgsList
                    .filter(ids => Array.isArray(ids) && ids.includes(statementId))
                    .map(ids =>
                        dispatch(
                            contentEditorApi.util.updateQueryData("getTopicStatementsData", ids, (draft: any) => {
                                const st = draft?.statements?.find((s: any) => s._id === statementId);
                                if (!st) return;
                                st.dictionary = st.dictionary || {};
                                st.dictionary[lang] = st.dictionary[lang] || {};
                                st.dictionary[lang].status = status;
                            })
                        )
                    );

                const listArgsList = contentEditorApi.util.selectCachedArgsForQuery(getState(), "getStatementsData") as (StatementsListQuery | undefined)[];

                const listPatches = listArgsList.map(args =>
                    dispatch(
                        contentEditorApi.util.updateQueryData("getStatementsData", args as any, (draft: any) => {
                            const st = draft?.docs?.find((s: any) => s._id === statementId);
                            if (!st) return;
                            st.dictionary = st.dictionary || {};
                            st.dictionary[lang] = st.dictionary[lang] || {};
                            st.dictionary[lang].status = status;
                        })
                    )
                );

                try {
                    await queryFulfilled;
                } catch {
                    topicPatches.forEach(p => p.undo());
                    listPatches.forEach(p => p.undo());
                }
            },
        }),

        updateStatementDictionaryFields: build.mutation<
            any,
            { statementId: string; lang: string; patch: Partial<{ negfemale: string; negmale: string; posfemale: string; posmale: string }> }
        >({
            query: ({ statementId, lang, patch }) => ({
                url: `${ENDPOINT_PREFIX}/statements/${statementId}/dictionary/${lang}`,
                method: "PATCH",
                body: patch,
            }),
            async onQueryStarted({ statementId, lang, patch }, { dispatch, getState, queryFulfilled }) {
                const topicArgsList = contentEditorApi.util.selectCachedArgsForQuery(getState(), "getTopicStatementsData") as string[][];

                const applyFieldsToStatement = (st: any) => {
                    if (!st) return;
                    st.dictionary = st.dictionary || {};
                    st.dictionary[lang] = st.dictionary[lang] || {};
                    if (patch.negfemale !== undefined) st.dictionary[lang].negfemale = patch.negfemale;
                    if (patch.negmale !== undefined) st.dictionary[lang].negmale = patch.negmale;
                    if (patch.posfemale !== undefined) st.dictionary[lang].posfemale = patch.posfemale;
                    if (patch.posmale !== undefined) st.dictionary[lang].posmale = patch.posmale;
                    st.dictionary[lang].status = "unverified";
                };

                const topicPatches = topicArgsList
                    .filter(ids => Array.isArray(ids) && ids.includes(statementId))
                    .map(ids =>
                        dispatch(
                            contentEditorApi.util.updateQueryData("getTopicStatementsData", ids, (draft: any) => {
                                const st = draft?.statements?.find((s: any) => s._id === statementId);
                                applyFieldsToStatement(st);
                            })
                        )
                    );

                const listArgsList = contentEditorApi.util.selectCachedArgsForQuery(getState(), "getStatementsData") as (StatementsListQuery | undefined)[];

                const listPatches = listArgsList.map(args =>
                    dispatch(
                        contentEditorApi.util.updateQueryData("getStatementsData", args as any, (draft: any) => {
                            const st = draft?.docs?.find((s: any) => s._id === statementId);
                            applyFieldsToStatement(st);
                        })
                    )
                );

                try {
                    await queryFulfilled;
                } catch {
                    topicPatches.forEach(p => p.undo());
                    listPatches.forEach(p => p.undo());
                }
            },
        }),

        updateTipDictionaryStatus: build.mutation<any, { tipId: string; lang: string; status: "verified" | "unverified" | "AI-generated" }>({
            query: ({ tipId, lang, status }) => ({
                url: `${ENDPOINT_PREFIX}/tips/${tipId}/dictionary/${lang}/status`,
                method: "PATCH",
                body: { status },
            }),
            async onQueryStarted({ tipId, lang, status }, { dispatch, getState, queryFulfilled }) {
                const argsList = contentEditorApi.util.selectCachedArgsForQuery(getState(), "getTopicTipsData") as string[][];

                const patches = argsList
                    .filter(ids => Array.isArray(ids) && ids.includes(tipId))
                    .map(ids =>
                        dispatch(
                            contentEditorApi.util.updateQueryData("getTopicTipsData", ids, (draft: any) => {
                                const t = draft?.tips?.find((x: any) => x._id === tipId);
                                if (!t) return;
                                t.dictionary = t.dictionary || {};
                                t.dictionary[lang] = t.dictionary[lang] || {};
                                t.dictionary[lang].status = status;
                            })
                        )
                    );

                try {
                    await queryFulfilled;
                } catch {
                    patches.forEach(p => p.undo());
                }
            },
        }),

        updateTipDictionaryFields: build.mutation<any, { tipId: string; lang: string; patch: Partial<{ text: string }> }>({
            query: ({ tipId, lang, patch }) => ({
                url: `${ENDPOINT_PREFIX}/tips/${tipId}/dictionary/${lang}`,
                method: "PATCH",
                body: patch,
            }),
            async onQueryStarted({ tipId, lang, patch }, { dispatch, getState, queryFulfilled }) {
                const argsList = contentEditorApi.util.selectCachedArgsForQuery(getState(), "getTopicTipsData") as string[][];

                const patches = argsList
                    .filter(ids => Array.isArray(ids) && ids.includes(tipId))
                    .map(ids =>
                        dispatch(
                            contentEditorApi.util.updateQueryData("getTopicTipsData", ids, (draft: any) => {
                                const t = draft?.tips?.find((x: any) => x._id === tipId);
                                if (!t) return;
                                t.dictionary = t.dictionary || {};
                                t.dictionary[lang] = t.dictionary[lang] || {};
                                if (patch.text !== undefined) t.dictionary[lang].text = patch.text;
                                t.dictionary[lang].status = "unverified";
                            })
                        )
                    );

                try {
                    await queryFulfilled;
                } catch {
                    patches.forEach(p => p.undo());
                }
            },
        }),

        getLevelsData: build.query<LevelsResponse, LevelsListQuery | void>({
            query: args => {
                const params = new URLSearchParams();
                const a = args ?? {};
                if (a.page) params.set("page", String(a.page));
                if (a.limit) params.set("limit", String(a.limit));
                if (a.moduleCode) params.set("moduleCode", a.moduleCode);
                if (a.topic) params.set("topic", a.topic);
                if (a.lang) params.set("lang", a.lang);
                if (a.status) params.set("status", a.status);
                if (a.q) params.set("q", a.q);
                if (a.sort) params.set("sort", a.sort);
                const qs = params.toString();
                return { url: `${ENDPOINT_PREFIX}/levels${qs ? `?${qs}` : ""}` };
            },
            providesTags: ["contentEditorLevels"],
        }),

        getLevelsCount: build.query<LevelsCountResponse, void>({
            query: () => ({ url: `${ENDPOINT_PREFIX}/levels/count` }),
            providesTags: ["contentEditorLevelsCount"],
        }),

        getLevelsFilters: build.query<LevelsFiltersResponse, void>({
            query: () => ({ url: `${ENDPOINT_PREFIX}/levels/filters` }),
            providesTags: ["contentEditorLevelsFilters"],
        }),

        getLevelById: build.query<LevelByIdResponse, string>({
            query: id => ({ url: `${ENDPOINT_PREFIX}/levels/${id}` }),
            providesTags: (_r, _e, id) => [{ type: "contentEditorLevelById", id }],
        }),

        updateLevelDictionaryStatus: build.mutation<
            any,
            { levelId: string; lang: string; status: "verified" | "unverified" | "AI-generated" | "AI-checked" | "AI-checked-with-numeric-mismatch" }
        >({
            query: ({ levelId, lang, status }) => ({
                url: `${ENDPOINT_PREFIX}/levels/${levelId}/dictionary/${lang}/status`,
                method: "PATCH",
                body: { status },
            }),
            async onQueryStarted({ levelId, lang, status }, { dispatch, getState, queryFulfilled }) {
                const listArgsList = contentEditorApi.util.selectCachedArgsForQuery(getState(), "getLevelsData") as (LevelsListQuery | undefined)[];

                const listPatches = listArgsList.map(args =>
                    dispatch(
                        contentEditorApi.util.updateQueryData("getLevelsData", args as any, (draft: any) => {
                            const lv = draft?.docs?.find((x: any) => x._id === levelId);
                            if (!lv) return;
                            lv.dictionary = lv.dictionary || {};
                            lv.dictionary[lang] = lv.dictionary[lang] || {};
                            lv.dictionary[lang].status = status;
                        })
                    )
                );
                const patchById = dispatch(
                    contentEditorApi.util.updateQueryData("getLevelById", levelId, (draft: any) => {
                        const lv = draft?.level;
                        if (lv) {
                            lv.dictionary = lv.dictionary || {};
                            lv.dictionary[lang] = lv.dictionary[lang] || {};
                            lv.dictionary[lang].status = status;
                        }
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    listPatches.forEach(p => p.undo());
                    patchById.undo();
                }
            },
        }),

        updateLevelDictionaryFields: build.mutation<any, UpdateLevelDictionaryFieldsBody>({
            query: ({ levelId, lang, patch }) => ({
                url: `${ENDPOINT_PREFIX}/levels/${levelId}/dictionary/${lang}`,
                method: "PATCH",
                body: patch,
            }),
            async onQueryStarted({ levelId, lang, patch }, { dispatch, getState, queryFulfilled }) {
                const applyListPatch = (lv: any) => {
                    if (!lv) return;
                    if (lang === "en") {
                        const prevEn = lv.dictionary?.en ?? {};
                        lv.dictionary = {
                            en: {
                                level_name: patch.level_name !== undefined ? patch.level_name : prevEn.level_name ?? "",
                                status: "verified",
                            },
                        };
                        lv.shouldTranslate = true;
                        return;
                    }
                    lv.dictionary = lv.dictionary || {};
                    lv.dictionary[lang] = lv.dictionary[lang] || {};
                    if (patch.level_name !== undefined) lv.dictionary[lang].level_name = patch.level_name;
                    lv.dictionary[lang].status = "unverified";
                };

                const applyFullPatch = (lv: any) => {
                    if (!lv) return;
                    if (lang === "en") {
                        const prevEn = lv.dictionary?.en ?? {};
                        const enEntry = {
                            level_name: patch.level_name !== undefined ? patch.level_name : prevEn.level_name ?? "",
                            tip_before: patch.tip_before !== undefined ? patch.tip_before : prevEn.tip_before ?? null,
                            tip_after: patch.tip_after !== undefined ? patch.tip_after : prevEn.tip_after ?? null,
                            status: "verified",
                        };
                        lv.dictionary = { en: enEntry };
                        lv.shouldTranslate = true;
                        return;
                    }
                    lv.dictionary = lv.dictionary || {};
                    lv.dictionary[lang] = lv.dictionary[lang] || {};
                    if (patch.level_name !== undefined) lv.dictionary[lang].level_name = patch.level_name;
                    if (patch.tip_before !== undefined) lv.dictionary[lang].tip_before = patch.tip_before;
                    if (patch.tip_after !== undefined) lv.dictionary[lang].tip_after = patch.tip_after;
                    lv.dictionary[lang].status = "unverified";
                };

                const listArgsList = contentEditorApi.util.selectCachedArgsForQuery(getState(), "getLevelsData") as (LevelsListQuery | undefined)[];

                const listPatches = listArgsList.map(args =>
                    dispatch(
                        contentEditorApi.util.updateQueryData("getLevelsData", args as any, (draft: any) => {
                            const lv = draft?.docs?.find((x: any) => x._id === levelId);
                            applyListPatch(lv);
                        })
                    )
                );
                const patchById = dispatch(
                    contentEditorApi.util.updateQueryData("getLevelById", levelId, (draft: any) => {
                        applyFullPatch(draft?.level);
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    listPatches.forEach(p => p.undo());
                    patchById.undo();
                }
            },
        }),

        updateTipBaseFields: build.mutation<any, UpdateTipBaseFieldsBody>({
            query: ({ tipId, patch }) => ({
                url: `${ENDPOINT_PREFIX}/tips/${tipId}/base`,
                method: "PATCH",
                body: patch,
            }),
            async onQueryStarted({ tipId, patch }, { dispatch, getState, queryFulfilled }) {
                const argsList = contentEditorApi.util.selectCachedArgsForQuery(getState(), "getTopicTipsData") as string[][];

                const patches = argsList
                    .filter(ids => Array.isArray(ids) && ids.includes(tipId))
                    .map(ids =>
                        dispatch(
                            contentEditorApi.util.updateQueryData("getTopicTipsData", ids, (draft: any) => {
                                const t = draft?.tips?.find((x: any) => x._id === tipId);
                                if (!t) return;

                                if ("placement" in patch) t.placement = patch.placement ?? null;
                                t.updatedAt = new Date().toISOString();
                            })
                        )
                    );

                try {
                    await queryFulfilled;
                } catch {
                    patches.forEach(p => p.undo());
                }
            },
        }),
    }),
});

export const {
    useGetTopicsDataQuery,
    useGetAssessmentsDataQuery,
    useGetModulesDataQuery,
    useGetContentEditLogsQuery,
    useGetTopicTipsDataQuery,
    useGetTopicStatementsDataQuery,
    useLazyGetTopicStatementsDataQuery,
    useLazyGetTopicTipsDataQuery,
    useUpdateTopicDictionaryStatusMutation,
    useUpdateTopicDictionaryFieldsMutation,
    useUpdateAssessmentDictionaryStatusMutation,
    useUpdateAssessmentDictionaryFieldsMutation,
    useUpdateAssessmentBaseFieldsMutation,
    useUpdateModuleDictionaryStatusMutation,
    useUpdateModuleDictionaryFieldsMutation,
    useGetLevelsDataQuery,
    useGetLevelsCountQuery,
    useGetLevelsFiltersQuery,
    useGetLevelByIdQuery,
    useUpdateLevelDictionaryStatusMutation,
    useUpdateLevelDictionaryFieldsMutation,
    useGetStatementsDataQuery,
    useGetStatementsCountQuery,
    useUpdateStatementDictionaryStatusMutation,
    useUpdateStatementDictionaryFieldsMutation,
    useUpdateTipDictionaryStatusMutation,
    useUpdateTipDictionaryFieldsMutation,
    useUpdateTipBaseFieldsMutation,
} = contentEditorApi;
